import { useContext, useEffect, useState } from 'react';

import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

import { fetchInviteResources, FetchInviteResources, InviteResources } from '../utils/fetchInviteResources';

interface Options {
  companyId: string;
  open: boolean;
  fetchResources?: FetchInviteResources;
}

const EMPTY_RESOURCES: InviteResources = { teams: [], locations: [], paySchedules: [] };

/**
 * The dialog loads its own teams / locations / pay schedules every time it opens. The Redux
 * company data is fetched once per company selection, skips sites while the position is
 * still loading and goes stale after a team or location is created — so a module could be
 * missing here although the user has every right to it.
 *
 * Nothing is gated by the caller's rights: inviting is open to every resource of the company,
 * the module screens are where permissions decide what is shown.
 */
export const useInviteResources = ({ companyId, open, fetchResources = fetchInviteResources }: Options) => {
  const { handleServerError } = useContext(NotificationContext);
  const [resources, setResources] = useState<InviteResources>(EMPTY_RESOURCES);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !companyId) {
      return;
    }
    let cancelled = false;
    setLoading(true);

    fetchResources(companyId)
      .then(result => {
        if (cancelled) return;
        setResources(result);
      })
      .catch(error => {
        if (cancelled) return;
        handleServerError(error);
        setResources(EMPTY_RESOURCES);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadedFor(companyId);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, companyId, fetchResources, handleServerError]);

  return {
    ...resources,
    loading,
    /** Inventories for the current company have arrived (possibly empty) */
    loaded: loadedFor === companyId && !loading,
  };
};
